/**
 * Shared chart formatters.
 *
 * Date and duration formatters are re-exported from `@/format/time` so
 * there is a single source of truth. Only `formatCompactNumber` lives
 * here because it is chart-specific (Y-axis tick labels).
 */

import type { CalendarDate } from "@internationalized/date";

import { formatCompactDate, formatLeadTime } from "@/format/time";

// ---------------------------------------------------------------------------
// Re-exports from format/time
// ---------------------------------------------------------------------------

export { formatLeadTime };

/**
 * Short date for X-axis labels (UTC).
 *
 * Delegates to `formatCompactDate` with `timeZone: "UTC"` so chart
 * timestamps render consistently regardless of the viewer's locale.
 *
 * Examples: "Jan 6", "Jan 6, 2024" (year included when not current year).
 */
export const formatChartAxisDate = (date: Date): string => formatCompactDate(date, "short", "UTC");

/**
 * Long date for tooltip headers (UTC, always includes year).
 *
 * Examples: "January 6, 2025".
 */
export const formatChartTooltipDate = (date: Date): string => {
    if (!date || isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    }).format(date);
};

// ---------------------------------------------------------------------------
// Chart-specific formatters
// ---------------------------------------------------------------------------

/**
 * Compact number formatter for Y-axis ticks.
 *
 * Examples: 0 → "0", 999 → "999", 1_000 → "1K", 1_500 → "1.5K",
 *           1_000_000 → "1M", 2_500_000 → "2.5M"
 */
export const formatCompactNumber = (n: number): string => {
    if (n === 0) return "0";

    const abs = Math.abs(n);
    const sign = n < 0 ? "-" : "";

    if (abs >= 1_000_000) {
        const v = abs / 1_000_000;
        return `${sign}${stripTrailingZero(v.toFixed(1))}M`;
    }
    if (abs >= 1_000) {
        const v = abs / 1_000;
        return `${sign}${stripTrailingZero(v.toFixed(1))}K`;
    }
    return String(n);
};

// ---------------------------------------------------------------------------
// X-axis interval
// ---------------------------------------------------------------------------

/**
 * Compute a numeric tick interval for category-axis charts so that
 * ~15 ticks are visible regardless of data length.
 *
 * Accepts the date range directly so that all charts sharing the same
 * range produce identical tick spacing.
 */
export const computeXAxisInterval = (start: CalendarDate, end: CalendarDate): number => {
    const startMs = new Date(`${start.toString()}T00:00:00Z`).getTime();
    const endMs = new Date(`${end.toString()}T00:00:00Z`).getTime();
    const days = Math.round((endMs - startMs) / 86_400_000) + 1;
    return Math.max(0, Math.round(days / 15) - 1);
};

// ---------------------------------------------------------------------------
// Tick generation
// ---------------------------------------------------------------------------

type NumericAxisScale = {
    ticks: number[];
    domain: [number, number];
};

/** Generate evenly-spaced tick positions from 0 to a rounded-up domain max. */
export const generateAxisTicks = (max: number, count = 5): NumericAxisScale => {
    if (max <= 0 || count < 2) return { ticks: [0], domain: [0, 1] };

    const intervals = count - 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)) - 1);
    const roundingUnit = magnitude * intervals;
    const domainMax = normalizeAxisNumber(Math.ceil(max / roundingUnit) * roundingUnit);
    const step = domainMax / intervals;

    return {
        ticks: Array.from({ length: count }, (_, i) => normalizeAxisNumber(i * step)),
        domain: [0, domainMax],
    };
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** "1.0" → "1", "1.5" → "1.5" */
const stripTrailingZero = (s: string): string => s.replace(/\.0$/, "");

const normalizeAxisNumber = (value: number): number => Number(value.toPrecision(12));
